import { Lightning, Colors, AppData } from '@lightningjs/sdk';
import { getImageTextureObj } from 'support/generalUtils';
import { STANDARD_FADE } from 'support/animations';
import { HoverableListItem } from 'components/common/HoverableListItem';
import { constants } from 'aliases';
import { NAV_ANIMATION_DURATION } from 'components/widgets/NavBar';
import TextLabel from 'components/common/TextLabel';

export interface NavBarTabTemplateSpec
  extends Lightning.Component.TemplateSpec {
  slug: string;
  icon: string;
  selected: boolean;
  title: string;
  showTitle: boolean;
  paddingTop: number;

  Icon: {
    Active: Lightning.Texture;
    Inactive: Lightning.Texture;
  };
  FocusIndicator: Lightning.Texture;
  Title: typeof TextLabel;
}

const INVISIBLE = constants.ui.invisible;
const HALF_VISIBLE = 0.5;
const VISIBLE = 1;

const ICON_SIZE_INACTIVE = 25;
const ICON_SIZE_ACTIVE = 40;

const ICON_MARGIN_LEFT_INACTIVE = 56;
const ICON_MARGIN_LEFT_ACTIVE = 49;
const ICON_MARGIN_RIGHT_INACTIVE = 10;
const ICON_MARGIN_RIGHT_ACTIVE = 20;

const FOCUS_INDICATOR_HEIGHT_INACTIVE = 25;
const FOCUS_INDICATOR_HEIGHT_ACTIVE = 40;

const FOCUS_INDICATOR_WIDTH_INACTIVE = 4;
const FOCUS_INDICATOR_WIDTH_ACTIVE = 4.58;

const FOCUS_INDICATOR_MARGIN_RIGHT = 30;

const ACTIVE_ICON_SCALE = 0.62;
const INACTIVE_ICON_SCALE = 1.62;

const TITLE_FONT_SIZE = 24;

const TAB_TRANSITION = {
  w: {
    duration: 0.3,
    timingFunction: 'ease-out',
  },
} as const;

const FOCUS_INDICATOR_ICON =
  'static/images/navigation/navigation-selection-indicator.svg';

export default class NavBarTab
  extends HoverableListItem<NavBarTabTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<NavBarTabTemplateSpec>
{
  private _slug = '';
  private _icon = '';
  private _selected = false;

  private _Icon = this.getByRef('Icon')!;
  private _ActiveIcon = this._Icon.getByRef('Active')!;
  private _InactiveIcon = this._Icon.getByRef('Inactive')!;

  private _FocusIndicator = this.getByRef('FocusIndicator')!;
  private _Title = this.getByRef('Title')!;

  set slug(slug: string) {
    this._slug = slug;
  }

  get slug() {
    return this._slug;
  }

  set icon(icon: string) {
    this._icon = icon;
    this.updateIconTextures();
    this.setActive(this.hasFocus());
  }

  set selected(selected: boolean) {
    this._selected = selected;
    this.updateFocusIndicator();
  }

  get title() {
    return this._Title.text?.text ?? '';
  }

  set title(title: string) {
    this._Title.patch({ text: { text: title } });
  }

  set showTitle(show: boolean) {
    this._Title.patch({ alpha: show ? VISIBLE : INVISIBLE });
  }

  set paddingTop(paddingTop: number) {
    this.patch({ y: paddingTop });
  }

  static override _template(): Lightning.Component.Template<NavBarTabTemplateSpec> {
    return {
      zIndex: constants.ui.zIndex.navLayer,
      flex: {
        direction: 'row',
        alignItems: 'center',
      },
      rect: true,
      color: Colors('transparent').get(),
      transitions: TAB_TRANSITION,
      Icon: {
        w: ICON_SIZE_ACTIVE,
        h: ICON_SIZE_ACTIVE,
        Active: {
          alpha: INVISIBLE,
        },
        Inactive: {
          alpha: HALF_VISIBLE,
        },
        flexItem: {
          marginLeft: ICON_MARGIN_LEFT_INACTIVE,
          marginRight: ICON_MARGIN_RIGHT_INACTIVE,
        },
      },
      FocusIndicator: {
        flexItem: {
          marginRight: FOCUS_INDICATOR_MARGIN_RIGHT,
        },
        alpha: INVISIBLE,
        w: FOCUS_INDICATOR_WIDTH_INACTIVE,
      },
      Title: {
        type: TextLabel,
        transitions: STANDARD_FADE,
        alpha: INVISIBLE,
        y: 2, // Offset for centering text
        text: {
          fontSize: TITLE_FONT_SIZE,
          textColor: Colors('text').alpha(HALF_VISIBLE).get(),
        },
      },
    };
  }

  override _getFocused() {
    return this;
  }

  override _focus() {
    this.updateFocusIndicator();
    this.updateIconAlpha();
    this.updateTitle();
  }

  override _unfocus() {
    this.updateFocusIndicator();
    this.updateIconAlpha();
    this.updateTitle();
  }

  override _handleEnter() {
    if (this._selected) return true; // prevent action

    return false; // bubble this up to the NavBar
  }

  override _handleHover() {
    if (AppData?.device.hoverDisabled()) {
      return true;
    }

    return super._handleHover(this);
  }

  override _handleClick() {
    if (this._selected) return true; // prevent action

    return false; // bubble this up to the NavBar
  }

  updateFocusIndicator() {
    if (!this._selected) {
      this._FocusIndicator.patch({ alpha: INVISIBLE });
    } else {
      const focusIndicatorTexture = getImageTextureObj(
        FOCUS_INDICATOR_ICON,
        FOCUS_INDICATOR_WIDTH_ACTIVE,
        FOCUS_INDICATOR_HEIGHT_ACTIVE,
      );
      this._FocusIndicator.patch({
        ...focusIndicatorTexture,
        alpha: this.hasFocus() ? VISIBLE : HALF_VISIBLE,
      });
    }
  }

  updateIconTextures() {
    if (!this._icon) return;

    const activeIconTexture = getImageTextureObj(
      this._icon,
      ICON_SIZE_ACTIVE,
      ICON_SIZE_ACTIVE,
    );
    this._ActiveIcon.patch({
      ...activeIconTexture,
      alpha: HALF_VISIBLE,
    });

    const inactiveIconTexture = getImageTextureObj(
      this._icon,
      ICON_SIZE_INACTIVE,
      ICON_SIZE_INACTIVE,
    );
    this._InactiveIcon.patch({ ...inactiveIconTexture, alpha: HALF_VISIBLE });
  }

  updateIconAlpha() {
    this._ActiveIcon.patch({ alpha: this.hasFocus() ? VISIBLE : HALF_VISIBLE });
  }

  setActive(isActive: boolean) {
    this._Icon.patch({
      smooth: {
        w: isActive ? ICON_SIZE_ACTIVE : ICON_SIZE_INACTIVE,
      },
      h: isActive ? ICON_SIZE_ACTIVE : ICON_SIZE_INACTIVE,
      flexItem: {
        marginLeft: isActive
          ? ICON_MARGIN_LEFT_ACTIVE
          : ICON_MARGIN_LEFT_INACTIVE,
        marginRight: isActive
          ? ICON_MARGIN_RIGHT_ACTIVE
          : ICON_MARGIN_RIGHT_INACTIVE,
      },
    });
    this._ActiveIcon.patch({ visible: isActive });
    this._InactiveIcon.patch({ visible: !isActive });

    // Animate smooth icon transitions with scale
    this._ActiveIcon.setSmooth('scale', isActive ? 1 : ACTIVE_ICON_SCALE, {
      duration: NAV_ANIMATION_DURATION,
    });
    this._InactiveIcon.setSmooth('scale', isActive ? INACTIVE_ICON_SCALE : 1, {
      duration: NAV_ANIMATION_DURATION,
    });

    // Animate smooth focus indicator transition
    this._FocusIndicator.setSmooth(
      'h',
      isActive
        ? FOCUS_INDICATOR_HEIGHT_ACTIVE
        : FOCUS_INDICATOR_HEIGHT_INACTIVE,
      {
        duration: NAV_ANIMATION_DURATION,
      },
    );
    this._FocusIndicator.setSmooth(
      'w',
      isActive ? FOCUS_INDICATOR_WIDTH_ACTIVE : FOCUS_INDICATOR_WIDTH_INACTIVE,
      {
        duration: NAV_ANIMATION_DURATION,
      },
    );
  }

  updateTitle() {
    const textColor = this.hasFocus()
      ? Colors('text').get()
      : Colors('text').alpha(HALF_VISIBLE).get();

    this._Title.patch({ text: { textColor } });
  }
}
